import React from 'react';
import { useSelector } from 'react-redux';
import Dropzone from 'react-dropzone';
import { StringHelpers } from '@salesperformanceint/e4shared';
import { uploadFileToAzure } from 'helpers/azure';
import { fileIcon, extension } from 'components/general/assets';
import config from 'config';

export const File = ({ assets, allowMultiple, onAdd, extensions, maxSize, folder, fileValidation, mediaOnly }) => {
    const [files, setFiles] = React.useState(assets);
    const [uploadProgress, setUploadProgress] = React.useState(100);
    const [uploading, setUploading] = React.useState('');
    const [error, setError] = React.useState('');
    const guid = useSelector(state => state.user.user.guid);
    const companyId = useSelector(state => state.user.user.company_Id);
    const partnerId = useSelector(state => state.user.user.partner_Id);

    React.useEffect(() => {
        files && onAdd(files.filter(f => f.name));
    }, [files]);

    const workingMaxSize = maxSize ? maxSize : config.MAX_ATTACHMENT_SIZE;

    const addFileToCollection = newFile => {
        if (allowMultiple) {
            setFiles(files.filter(f => f.name !== newFile.name).concat(newFile));
        } else {
            setFiles([newFile]);
        }
    };

    const effectivePartner = folder === 'linkedIn_Files' ? 0 : partnerId;

    const addFile = file => {
        const newFile = {
            ...file,
            name: file.name,
            size: file.size,
            companyId,
            effectivePartner,
            url: file.uploadInfo.url,
        };
        addFileToCollection(newFile);
    };

    const removeFile = file => {
        setFiles(files.filter(f => f.name !== file.name));
    };

    const onDrop = async uploadedFiles => {
        setError('');
        setUploadProgress(1);
        let hasError = false;

        uploadedFiles.forEach(async (f, i) => {
            setUploading(f.name);
            const extraError = fileValidation ? fileValidation(f) : '';
            if (extensions && !StringHelpers.containsExt(f.name, extensions)) {
                hasError = true;
                setError(
                    `Sorry, but you must upload a file with an extension of ${StringHelpers.arrayToString(
                        extensions,
                        ',',
                        'or'
                    )}`
                );
            } else if (mediaOnly && !StringHelpers.containsExt(f.name, config.mediaExtensions)) {
                hasError = true;
                setError(
                    `Sorry, but as you have selected to launch this in the video player, you must upload a file with an extension of ${StringHelpers.arrayToString(
                        config.mediaExtensions,
                        ',',
                        'or'
                    )}`
                );
            } else if (f.size > workingMaxSize) {
                hasError = true;
                setError(`Sorry, but this file is too large (max. ${StringHelpers.bytesToSize(workingMaxSize)}).`);
            } else if (extraError) {
                hasError = true;
                setError(extraError);
            } else if (files.some(file => file.name === f.name)) {
                hasError = true;
                setError('You have already uploaded a file with this name');
            } else {
                addFileToCollection(f);

                await uploadFileToAzure(
                    guid,
                    f,
                    folder,
                    progress => {
                        setUploadProgress(parseInt(progress, 10));
                    },
                    uploadResponse => {
                        setUploading('');
                        f.uploadInfo = uploadResponse;
                        f.path = uploadResponse.path;
                        f.folder = uploadResponse.folder;
                        addFile(f);
                    }
                );
            }

            if (hasError) {
                setUploading('');
                removeFile(f);
            }
        });
    };

    return (
        <div>
            <div className="dropzone-container">
                <Dropzone
                    inputProps={{ placeholder: 'Upload a file' }}
                    onDrop={files => onDrop(files)}
                    multiple={false}
                    className="dropzone"
                    activeClassName="active-dropzone"
                    disabled={uploading !== ''}
                    accept={extensions ? extensions.join(',') : ''}
                />
                <div className="dropzone-help">
                    <div className="hidden-xs">
                        Upload by dragging your file into, or clicking on this box.
                        {allowMultiple && <span>If you need to upload multiple files, simply repeat for each file.</span>}
                        <br />
                        (Max file size: {StringHelpers.bytesToSize(workingMaxSize)})
                        {error && <div className="text-danger mt10">{error}</div>}
                    </div>
                    <div className="hidden visible-xs">
                        <i className="mr5 fa fa-file" />
                    </div>
                </div>
            </div>
            {files.length > 0 && (
                <div className="mt10">
                    <label>Files</label>
                    {files
                        .sort((a, b) => (a.name > b.name ? 1 : -1))
                        .map((f, i) => {
                            return (
                                <div key={`file-${i}`} className="mb5 flex flex-center">
                                    <i className={`fa fa-${fileIcon(extension(f))} mr5`} />
                                    <span className="ellipsis500 flex-grow">
                                        File {f.name} - {StringHelpers.bytesToSize(f.size)}
                                    </span>
                                    <i
                                        className="ml5 text-danger fa fa-trash pointer text-right"
                                        title={`Remove the file ${f.name}`}
                                        onClick={() => removeFile(f)}
                                    />
                                </div>
                            );
                        })}
                    {uploadProgress < 100 && (
                        <div className="flex flex-center">
                            <div className="flex-grow file-progress">
                                <div style={{ width: `${uploadProgress}%` }}></div>
                            </div>
                            <div className="ml5">{uploadProgress}%</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default File;
